/**
 * Curated routine-based blend ideas: taste and ritual language only (not medical).
 * @typedef {{ slug: string, name: string, headline: string, routineCopy: string, preferredBases: ('gold'|'purple')[], ingredientIds: string[], seoTitle: string, seoDescription: string }} BlendIdea
 */

/** @type {BlendIdea[]} */
export const BLENDS = [
  {
    slug: "daily-glow",
    name: "Daily Glow",
    headline: "A bright daily ritual",
    routineCopy:
      "Popular in bright morning routines for tart florals and warm golden notes. Shipped MossMatrix gels use powdered botanicals only; stir a spoonful into oats, yoghurt, coconut water, tea, or shakes at home.",
    preferredBases: ["gold", "purple"],
    ingredientIds: ["hibiscus", "rosehip", "turmeric"],
    seoTitle: "Daily Glow blend | Hibiscus, rosehip and turmeric with sea moss",
    seoDescription:
      "Explore the Daily Glow routine: hibiscus, rosehip, and turmeric with MossMatrix sea moss gel. Bright routine language, herb-based blends, and ideas for home use.",
  },
  {
    slug: "gut-friendly-ritual",
    name: "Warm Kitchen Ritual",
    headline: "Warm, earthy daily routines",
    routineCopy:
      "Often chosen for warm, earthy kitchen habits. Ginger’s zip, cinnamon’s cosiness, and dandelion root’s roasted depth sit neatly on a mild gold base. Powders in the jar; add milk or tea at home.",
    preferredBases: ["gold"],
    ingredientIds: ["ginger", "cinnamon", "dandelion-root"],
    seoTitle: "Warm kitchen ritual | Ginger, cinnamon and dandelion root",
    seoDescription:
      "A warming herb trio for everyday kitchen rituals with MossMatrix gold sea moss gel. Careful wellness wording, no medical claims.",
  },
  {
    slug: "immune-season-blend",
    name: "Seasonal Comfort",
    headline: "Rich seasonal notes for colder months",
    routineCopy:
      "A rich, tart seasonal line for cooler evenings and cosy cups. Purple base optional, with elderberry depth and warming ginger and turmeric. Dried botanicals only in shipped gel; fresh fruit stays in your own glass.",
    preferredBases: ["purple"],
    ingredientIds: ["elderberry", "ginger", "turmeric"],
    seoTitle: "Seasonal comfort | Elderberry, ginger and turmeric",
    seoDescription:
      "Seasonal elderberry, ginger, and turmeric routine ideas with MossMatrix purple sea moss gel. Educational, non-medical.",
  },
  {
    slug: "morning-lift",
    name: "Morning Lift",
    headline: "Clean morning lift without the hype",
    routineCopy:
      "A clean morning line with earthy ingredients. Maca’s malty note, moringa’s green boldness, and a trace of ginger for warmth pair well with gold base, then fold into shakes, oats, or coconut milk at home.",
    preferredBases: ["gold"],
    ingredientIds: ["maca", "moringa", "ginger"],
    seoTitle: "Morning lift | Maca, moringa and ginger sea moss routine",
    seoDescription:
      "Morning Lift: maca, moringa, and ginger with gold sea moss gel. Earthy profile, part of a balanced lifestyle, not a stimulant claim.",
  },
  {
    slug: "calm-evening-ritual",
    name: "Calm Evening Ritual",
    headline: "Soft evening wind-down",
    routineCopy:
      "A soft evening ritual with gentle floral spice on the spoon. Chamomile’s hay-like florals, ashwagandha’s earthy depth, and a whisper of cinnamon for cosiness. Check cautions on the ingredient pages if you take medication or are pregnant.",
    preferredBases: ["purple"],
    ingredientIds: ["ashwagandha", "chamomile", "cinnamon"],
    seoTitle: "Calm evening ritual | Ashwagandha, chamomile and cinnamon",
    seoDescription:
      "Evening routine inspiration with ashwagandha, chamomile, and cinnamon. Educational cautions apply; not medical advice.",
  },
  {
    slug: "green-mineral",
    name: "Green Mineral",
    headline: "Mineral-rich botanicals, green on the spoon",
    routineCopy:
      "A bold green, earthy botanical line. Moringa, spirulina, and nettle are often chosen when people want a vivid spoonful in plant milk or shakes they blend at home. No fresh greens shipped inside the gel.",
    preferredBases: ["gold"],
    ingredientIds: ["moringa", "spirulina", "nettle"],
    seoTitle: "Green mineral | Moringa, spirulina and nettle",
    seoDescription:
      "Green Mineral blend ideas with moringa, spirulina, and nettle. Herb-based MossMatrix gels and home-use suggestions only.",
  },
  {
    slug: "warming-root-blend",
    name: "Warming Root Blend",
    headline: "Bold, earthy, traditional-feeling",
    routineCopy:
      "Warm, bold, and earthy: a classic kitchen trio of ginger, turmeric, and black pepper for flavour-led habits. Traditionally paired in cooking; we keep it about taste and colour on the spoon.",
    preferredBases: ["gold"],
    ingredientIds: ["ginger", "turmeric", "black-pepper"],
    seoTitle: "Warming root blend | Ginger, turmeric and black pepper",
    seoDescription:
      "Warming root routine with ginger, turmeric, and black pepper. MossMatrix custom sea moss blends, honest wellness copy.",
  },
  {
    slug: "botanical-berry-style",
    name: "Botanical Berry-Style",
    headline: "Tart, floral, naturally deep",
    routineCopy:
      "A tart, floral botanical line with real depth on the tongue. Elderberry, hibiscus, and rosehip read like a berry-style cordial while staying powder-only in shipped gel. Add fresh berries at home if you like.",
    preferredBases: ["purple"],
    ingredientIds: ["elderberry", "hibiscus", "rosehip"],
    seoTitle: "Botanical berry-style | Elderberry, hibiscus and rosehip",
    seoDescription:
      "Berry-style botanical routine with elderberry, hibiscus, and rosehip. Tart floral profile for purple sea moss gel builds.",
  },
];

/** @param {string} slug */
export function getBlendBySlug(slug) {
  return BLENDS.find((b) => b.slug === slug) || null;
}

/** @param {string} ingredientId */
export function getBlendsUsingIngredient(ingredientId) {
  return BLENDS.filter((b) => b.ingredientIds.includes(ingredientId));
}
